import React, { useState, useEffect } from 'react';
import { Plus, Wheat, FlaskConical, Trash2, Lightbulb, Pill, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { productsAPI, formulasAPI, materialsAPI, medicationsAPI } from '../lib/api';
import api from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { toast } from 'sonner';
import { useSuperUser } from '../context/SuperUserContext';
import { SuperUserDialog } from '../components/SuperUserDialog';
import { ProductsTable, FormulasGrid, PremixTable } from '../components/products';

const FORMULA_TYPES = ['Iniciación', 'Crecimiento', 'Engorde', 'Postura', 'Reproductoras', 'Pre-Inicio', 'Finalización', 'Mantenimiento', 'Otro'];
const ANIMAL_TYPES = [{ value: 'pollo', label: 'Pollo' }, { value: 'gallina', label: 'Gallina' }, { value: 'cerdo', label: 'Cerdo' }, { value: 'becerro', label: 'Becerro' }];

// Colores para badges de animales
const ANIMAL_COLORS = {
  'pollo': 'bg-red-100 text-red-800',
  'gallina': 'bg-green-100 text-green-800',
  'cerdo': 'bg-pink-100 text-pink-800',
  'becerro': 'bg-amber-100 text-amber-800'
};

// Factores de conversión a libras
const UNIT_TO_LBS = {
  'lb': 1,
  'kg': 2.20462,
  'qq': 100,
  'saco': 100 // Por defecto saco = 100 lbs
};

export default function ProductsPage() {
  const [activeTab, setActiveTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [formulas, setFormulas] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [premixMedications, setPremixMedications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedFormula, setExpandedFormula] = useState(null);
  
  const { isSuperUser } = useSuperUser();
  const [superUserDialogOpen, setSuperUserDialogOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  
  // Product form
  const [productFormData, setProductFormData] = useState({ name: '', animal_type: '', formula_id: '', price_dop: 0, price_usd: 0, unit: 'qq' });
  
  // Formula form
  const [formulaDialogOpen, setFormulaDialogOpen] = useState(false);
  const [editingFormula, setEditingFormula] = useState(null);
  const [formulaFormData, setFormulaFormData] = useState({ name: '', animal_type: '', type: '', output_quantity: 1, output_unit: 'qq', ingredients: [] });
  const [newIngredient, setNewIngredient] = useState({ material_id: '', quantity: 0, unit: 'qq' });
  
  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState(null);
  const [operationalCostPerUnit, setOperationalCostPerUnit] = useState(0);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [productsRes, formulasRes, materialsRes, medicationsRes] = await Promise.all([
        productsAPI.getAll(), formulasAPI.getAll(), materialsAPI.getAll(), medicationsAPI.getAll(),
      ]);
      setProducts(productsRes.data);
      setFormulas(formulasRes.data);
      setMaterials(materialsRes.data);
      setPremixMedications(medicationsRes.data.filter(m => m.administration_type === 'premix'));
      // Obtener costo operativo desde Settings (configuración fija)
      try {
        const costSettings = await api.get('/settings/operational-cost');
        setOperationalCostPerUnit(costSettings.data.cost_per_qq || 0);
      } catch { setOperationalCostPerUnit(0); }
    } catch { toast.error('Error al cargar datos'); } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);
  
  // Recargar datos cuando la ventana obtiene foco (ej: después de cambiar precios en Inventario)
  useEffect(() => {
    const handleFocus = () => { fetchData(); };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  const resetProductForm = () => setProductFormData({ name: '', animal_type: '', formula_id: '', price_dop: 0, price_usd: 0, unit: 'qq' });
  
  // Filtrar fórmulas por animal seleccionado
  const filteredFormulas = productFormData.animal_type 
    ? formulas.filter(f => f.animal_type === productFormData.animal_type)
    : formulas;
  
  const handleProductSubmit = async (e) => {
    e.preventDefault();
    try {
      await productsAPI.create({ ...productFormData, formula_id: productFormData.formula_id || null });
      toast.success('Producto creado');
      resetProductForm();
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Error al crear producto'); }
  };

  const handleDeleteProduct = (product) => {
    if (!isSuperUser) { setPendingAction({ type: 'delete_product', data: product }); setSuperUserDialogOpen(true); return; }
    setItemToDelete(product); setDeleteType('product'); setDeleteDialogOpen(true);
  };

  const handleUpdateProductPrice = async (product) => {
    try {
      await productsAPI.update(product.id, {
        name: product.name,
        formula_id: product.formula_id,
        price_usd: parseFloat(product.suggested_price_usd.toFixed(2)),
        price_dop: parseFloat(product.suggested_price_dop.toFixed(2)),
        unit: product.unit
      });
      toast.success(`Precio de "${product.name}" actualizado a US$ ${product.suggested_price_usd.toFixed(2)}`);
      fetchData();
    } catch (error) { 
      toast.error(error.response?.data?.detail || 'Error al actualizar precio'); 
    }
  };

  const resetFormulaForm = () => {
    setFormulaFormData({ name: '', animal_type: '', type: '', output_quantity: 1, output_unit: 'qq', ingredients: [] });
    setNewIngredient({ material_id: '', quantity: 0, unit: 'qq' });
    setEditingFormula(null); setFormulaDialogOpen(false);
  };

  const handleEditFormula = (formula) => {
    if (!isSuperUser) { setPendingAction({ type: 'edit_formula', data: formula }); setSuperUserDialogOpen(true); return; }
    executeEditFormula(formula);
  };

  const executeEditFormula = (formula) => {
    setEditingFormula(formula);
    setFormulaFormData({
      name: formula.name, animal_type: formula.animal_type || '', type: formula.type,
      output_quantity: formula.output_quantity, output_unit: formula.output_unit,
      ingredients: formula.ingredients.map(ing => ({ material_id: ing.material_id, material_name: ing.material_name, quantity: ing.quantity, unit: ing.unit }))
    });
    setFormulaDialogOpen(true);
  };

  const handleAddIngredient = () => {
    if (!newIngredient.material_id || newIngredient.quantity <= 0) { toast.error('Selecciona un material y cantidad válida'); return; }
    const material = materials.find(m => m.id === newIngredient.material_id);
    if (!material) return;
    if (formulaFormData.ingredients.some(i => i.material_id === newIngredient.material_id)) { toast.error('Este material ya está en la fórmula'); return; }
    setFormulaFormData({ ...formulaFormData, ingredients: [...formulaFormData.ingredients, { ...newIngredient, material_name: material.name }] });
    setNewIngredient({ material_id: '', quantity: 0, unit: 'qq' });
  };

  const handleRemoveIngredient = (materialId) => setFormulaFormData({ ...formulaFormData, ingredients: formulaFormData.ingredients.filter(i => i.material_id !== materialId) });

  const handleFormulaSubmit = async (e) => {
    e.preventDefault();
    if (formulaFormData.ingredients.length === 0) { toast.error('Agrega al menos un ingrediente'); return; }
    try {
      if (editingFormula) { await formulasAPI.update(editingFormula.id, formulaFormData); toast.success('Fórmula actualizada'); }
      else { await formulasAPI.create(formulaFormData); toast.success('Fórmula creada'); }
      resetFormulaForm(); fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Error al guardar fórmula'); }
  };

  const handleDeleteFormula = (formula) => {
    if (!isSuperUser) { setPendingAction({ type: 'delete_formula', data: formula }); setSuperUserDialogOpen(true); return; }
    setItemToDelete(formula); setDeleteType('formula'); setDeleteDialogOpen(true);
  };

  const handleSuperUserVerified = () => {
    if (!pendingAction) return;
    switch (pendingAction.type) {
      case 'delete_product': setItemToDelete(pendingAction.data); setDeleteType('product'); setDeleteDialogOpen(true); break;
      case 'edit_formula': executeEditFormula(pendingAction.data); break;
      case 'delete_formula': setItemToDelete(pendingAction.data); setDeleteType('formula'); setDeleteDialogOpen(true); break;
      default: break;
    }
    setPendingAction(null);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    try {
      if (deleteType === 'product') { await productsAPI.delete(itemToDelete.id); toast.success('Producto eliminado'); }
      else { await formulasAPI.delete(itemToDelete.id); toast.success('Fórmula eliminada'); }
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Error al eliminar'); }
    finally { setDeleteDialogOpen(false); setItemToDelete(null); setDeleteType(null); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin h-8 w-8 border-4 border-[#931A25] border-t-transparent rounded-full"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="products-page">
      <div><h1 className="text-2xl md:text-3xl font-bold text-slate-900">Alimento</h1><p className="text-slate-500 mt-1">Gestión de productos y fórmulas de alimento balanceado</p></div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <TabsList className="grid w-full grid-cols-3 max-w-lg">
            <TabsTrigger value="products" className="flex items-center gap-2"><Wheat className="h-4 w-4" />Productos ({products.length})</TabsTrigger>
            <TabsTrigger value="formulas" className="flex items-center gap-2"><FlaskConical className="h-4 w-4" />Fórmulas ({formulas.length})</TabsTrigger>
            <TabsTrigger value="premix" className="flex items-center gap-2"><Pill className="h-4 w-4" />Premix ({premixMedications.length})</TabsTrigger>
          </TabsList>
          <Button variant="outline" size="sm" onClick={() => { fetchData(); toast.success('Precios actualizados'); }} className="text-slate-600 hover:text-slate-800">
            <RefreshCw className="h-4 w-4 mr-1" /> Actualizar Precios
          </Button>
        </div>

        {/* PRODUCTOS TAB */}
        <TabsContent value="products" className="space-y-4 mt-4">
          <div className="flex justify-end">
            <Dialog onOpenChange={(open) => !open && resetProductForm()}>
              <DialogTrigger asChild><Button className="bg-[#931A25] hover:bg-[#7A151F]" data-testid="add-product-btn"><Plus className="h-4 w-4 mr-2" />Nuevo Producto</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Nuevo Producto de Alimento</DialogTitle></DialogHeader>
                <form onSubmit={handleProductSubmit} className="space-y-4">
                  <div className="space-y-2"><Label>Nombre del Producto *</Label><Input value={productFormData.name} onChange={(e) => setProductFormData({...productFormData, name: e.target.value})} placeholder="Ej: Alimento Pollos Engorde" required /></div>
                  <div className="space-y-2">
                    <Label>Animal *</Label>
                    <Select value={productFormData.animal_type || "none"} onValueChange={(v) => setProductFormData({...productFormData, animal_type: v === "none" ? "" : v, formula_id: ""})}>
                      <SelectTrigger><SelectValue placeholder="Seleccionar animal..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Seleccionar animal...</SelectItem>
                        {ANIMAL_TYPES.map((animal) => <SelectItem key={animal.value} value={animal.value}>{animal.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Fórmula Asociada (opcional)</Label>
                    <Select 
                      value={productFormData.formula_id || "none"} 
                      onValueChange={(v) => setProductFormData({...productFormData, formula_id: v === "none" ? "" : v})}
                      disabled={!productFormData.animal_type}
                    >
                      <SelectTrigger className={!productFormData.animal_type ? "opacity-50" : ""}>
                        <SelectValue placeholder={productFormData.animal_type ? "Seleccionar fórmula" : "Primero selecciona un animal"} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin fórmula</SelectItem>
                        {filteredFormulas.length === 0 && productFormData.animal_type ? (
                          <SelectItem value="no-formulas" disabled>No hay fórmulas para {ANIMAL_TYPES.find(a => a.value === productFormData.animal_type)?.label}</SelectItem>
                        ) : (
                          filteredFormulas.map((f) => <SelectItem key={f.id} value={f.id}>{f.name} ({f.type}) - US$ {f.unit_cost_usd?.toFixed(2) || '0.00'}</SelectItem>)
                        )}
                      </SelectContent>
                    </Select>
                    {productFormData.animal_type && filteredFormulas.length === 0 && (
                      <p className="text-xs text-amber-600">No hay fórmulas registradas para este animal. Puedes crear una en la pestaña "Fórmulas".</p>
                    )}
                  </div>
                  {productFormData.formula_id && (() => {
                    const selectedFormula = formulas.find(f => f.id === productFormData.formula_id);
                    if (!selectedFormula) return null;
                    // unit_cost_usd de fórmula es SOLO materiales
                    const formulaCostUSD = selectedFormula.unit_cost_usd || 0;
                    const formulaCostDOP = selectedFormula.unit_cost || 0;
                    // Total = fórmula + operativo
                    const totalCostUSD = formulaCostUSD + operationalCostPerUnit;
                    const totalCostDOP = formulaCostDOP + (operationalCostPerUnit * 62);
                    return (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <p className="text-sm font-medium text-green-800 mb-2 flex items-center gap-2"><Lightbulb className="h-4 w-4 text-yellow-500" />Precio Sugerido (Fórmula + Costos Operativos)</p>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div><p className="text-slate-500">Costo Fórmula Asociada:</p><p className="font-semibold">US$ {formulaCostUSD.toFixed(2)}</p><p className="text-xs text-green-600">({formatCurrency(formulaCostDOP)})</p></div>
                          <div><p className="text-slate-500">Costo Operativo/qq:</p><p className="font-semibold">US$ {operationalCostPerUnit.toFixed(2)}</p><p className="text-xs text-green-600">({formatCurrency(operationalCostPerUnit * 62)})</p></div>
                        </div>
                        <div className="mt-2 pt-2 border-t border-green-200"><p className="text-slate-500 text-sm">Total Costo Producción:</p><p className="font-bold text-lg text-green-700">US$ {totalCostUSD.toFixed(2)}</p><p className="text-sm text-green-600">({formatCurrency(totalCostDOP)})</p></div>
                        <Button type="button" variant="outline" size="sm" className="mt-2 w-full border-green-300 text-green-700 hover:bg-green-100" onClick={() => setProductFormData({...productFormData, price_usd: parseFloat(totalCostUSD.toFixed(2)), price_dop: parseFloat(totalCostDOP.toFixed(2))})}>Usar Precio Sugerido</Button>
                      </div>
                    );
                  })()}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2"><Label>Precio DOP (RD$) *</Label><Input type="number" step="0.01" min="0" value={productFormData.price_dop} onChange={(e) => setProductFormData({...productFormData, price_dop: parseFloat(e.target.value) || 0})} required /></div>
                    <div className="space-y-2"><Label>Precio USD (US$) *</Label><Input type="number" step="0.01" min="0" value={productFormData.price_usd} onChange={(e) => setProductFormData({...productFormData, price_usd: parseFloat(e.target.value) || 0})} required /></div>
                  </div>
                  <div className="space-y-2">
                    <Label>Unidad de Venta</Label>
                    <Select value={productFormData.unit} onValueChange={(v) => setProductFormData({...productFormData, unit: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="qq">Quintal (qq)</SelectItem><SelectItem value="lb">Libra (lb)</SelectItem><SelectItem value="kg">Kilogramo (kg)</SelectItem><SelectItem value="saco">Saco</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <DialogFooter><DialogClose asChild><Button type="button" variant="outline">Cancelar</Button></DialogClose><Button type="submit" className="bg-[#931A25] hover:bg-[#7A151F]">Guardar</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          <ProductsTable products={products} formulas={formulas} isSuperUser={isSuperUser} onDelete={handleDeleteProduct} operationalCostPerUnit={operationalCostPerUnit} />
        </TabsContent>

        {/* FÓRMULAS TAB */}
        <TabsContent value="formulas" className="space-y-4 mt-4">
          <div className="flex justify-end">
            <Dialog open={formulaDialogOpen} onOpenChange={(open) => { setFormulaDialogOpen(open); if (!open) resetFormulaForm(); }}>
              <DialogTrigger asChild><Button className="bg-[#931A25] hover:bg-[#7A151F]" data-testid="add-formula-btn"><Plus className="h-4 w-4 mr-2" />Nueva Fórmula</Button></DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>{editingFormula ? 'Editar Fórmula' : 'Crear Fórmula'}</DialogTitle></DialogHeader>
                <form onSubmit={handleFormulaSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2"><Label>Nombre *</Label><Input value={formulaFormData.name} onChange={(e) => setFormulaFormData({...formulaFormData, name: e.target.value})} placeholder="Ej: Engorde Fase 1" required /></div>
                    <div className="space-y-2"><Label>Animal *</Label>
                      <Select value={formulaFormData.animal_type} onValueChange={(v) => setFormulaFormData({...formulaFormData, animal_type: v})}>
                        <SelectTrigger><SelectValue placeholder="Seleccionar animal..." /></SelectTrigger>
                        <SelectContent>{ANIMAL_TYPES.map((animal) => <SelectItem key={animal.value} value={animal.value}>{animal.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2"><Label>Etapa/Tipo *</Label>
                      <Select value={formulaFormData.type} onValueChange={(v) => setFormulaFormData({...formulaFormData, type: v})}>
                        <SelectTrigger><SelectValue placeholder="Seleccionar etapa..." /></SelectTrigger>
                        <SelectContent>{FORMULA_TYPES.map((type) => <SelectItem key={type} value={type}>{type}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2"><Label>Cantidad Producida</Label><Input type="number" step="0.01" value={formulaFormData.output_quantity} onChange={(e) => setFormulaFormData({...formulaFormData, output_quantity: parseFloat(e.target.value) || 1})} onFocus={(e) => e.target.select()} required /></div>
                    <div className="space-y-2"><Label>Unidad</Label>
                      <Select value={formulaFormData.output_unit} onValueChange={(v) => setFormulaFormData({...formulaFormData, output_unit: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="qq">Quintales (qq)</SelectItem><SelectItem value="lb">Libras (lb)</SelectItem><SelectItem value="kg">Kilogramos (kg)</SelectItem><SelectItem value="saco">Sacos</SelectItem></SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="border-t pt-4">
                    <Label className="text-base font-semibold">Ingredientes</Label>
                    <div className="mt-3 grid grid-cols-12 gap-2 items-end">
                      <div className="col-span-5"><Label className="text-xs">Material</Label>
                        <Select value={newIngredient.material_id} onValueChange={(v) => { const selectedMaterial = materials.find(m => m.id === v); setNewIngredient({...newIngredient, material_id: v, unit: selectedMaterial?.unit || 'qq'}); }}>
                          <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                          <SelectContent>{materials.map((m) => <SelectItem key={m.id} value={m.id}>{m.name} ({m.unit})</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-3"><Label className="text-xs">Cantidad</Label><Input type="number" step="0.01" value={newIngredient.quantity} onChange={(e) => setNewIngredient({...newIngredient, quantity: parseFloat(e.target.value) || 0})} /></div>
                      <div className="col-span-2"><Label className="text-xs">Unidad</Label>
                        <Select value={newIngredient.unit} onValueChange={(v) => setNewIngredient({...newIngredient, unit: v})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="qq">qq</SelectItem><SelectItem value="lb">lb</SelectItem><SelectItem value="kg">kg</SelectItem><SelectItem value="saco">saco</SelectItem></SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2"><Button type="button" onClick={handleAddIngredient} className="w-full bg-[#E97171] hover:bg-[#931A25]"><Plus className="h-4 w-4" /></Button></div>
                    </div>
                    {formulaFormData.ingredients.length > 0 && (
                      <div className="mt-4 border rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50"><tr><th className="text-left p-2">Material</th><th className="text-right p-2">Cantidad</th><th className="text-center p-2">Unidad</th><th className="text-right p-2">Lbs</th><th className="text-center p-2"></th></tr></thead>
                          <tbody>{formulaFormData.ingredients.map((ing) => {
                            const lbsQty = ing.quantity * (UNIT_TO_LBS[ing.unit] || 1);
                            return (
                            <tr key={ing.material_id} className="border-t"><td className="p-2">{ing.material_name}</td><td className="p-2 text-right">{ing.quantity}</td><td className="p-2 text-center">{ing.unit}</td><td className="p-2 text-right font-medium">{lbsQty.toFixed(2)}</td><td className="p-2 text-center"><Button type="button" variant="ghost" size="sm" onClick={() => handleRemoveIngredient(ing.material_id)} className="text-red-600"><Trash2 className="h-4 w-4" /></Button></td></tr>
                            );
                          })}</tbody>
                          <tfoot className="bg-slate-100 font-semibold">
                            <tr>
                              <td className="p-2" colSpan={3}>Total Libras:</td>
                              <td className="p-2 text-right text-[#931A25]">{formulaFormData.ingredients.reduce((sum, ing) => sum + (ing.quantity * (UNIT_TO_LBS[ing.unit] || 1)), 0).toFixed(2)} lbs</td>
                              <td></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                  </div>
                  <DialogFooter><DialogClose asChild><Button type="button" variant="outline">Cancelar</Button></DialogClose><Button type="submit" className="bg-[#931A25] hover:bg-[#7A151F]">{editingFormula ? 'Actualizar' : 'Guardar'}</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          <FormulasGrid formulas={formulas} expandedFormula={expandedFormula} setExpandedFormula={setExpandedFormula} isSuperUser={isSuperUser} onEdit={handleEditFormula} onDelete={handleDeleteFormula} />
        </TabsContent>

        {/* PREMIX TAB */}
        <TabsContent value="premix" className="space-y-4 mt-4">
          <PremixTable premixMedications={premixMedications} />
        </TabsContent>
      </Tabs>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>¿Confirmar eliminación?</AlertDialogTitle><AlertDialogDescription>Esta acción eliminará "{itemToDelete?.name}" permanentemente. Esta acción no se puede deshacer.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <SuperUserDialog open={superUserDialogOpen} onOpenChange={setSuperUserDialogOpen} onSuccess={handleSuperUserVerified} title="Acción Restringida" description="Esta acción requiere verificación de super usuario." />
    </div>
  );
}
